import { createClient } from '@supabase/supabase-js'

// Get environment variables for Vite
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
const supabaseServiceRole = import.meta.env.VITE_SUPABASE_SERVICE_ROLE

// Validate required environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing required Supabase environment variables. Check your .env file.')
}

// Client-side instance for browser operations
export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Admin client for server-side operations (use with caution in client-side apps)
export const supabaseAdmin = supabaseServiceRole 
  ? createClient(supabaseUrl, supabaseServiceRole)
  : null

// Storage bucket names
export const VIDEOS_BUCKET = import.meta.env.VITE_SUPABASE_VIDEOS_BUCKET || 'videos'
export const OUTPUTS_BUCKET = import.meta.env.VITE_SUPABASE_OUTPUTS_BUCKET || 'outputs'

// Storage utility functions
export const storage = {
  // Upload file to storage
  async uploadFile(bucket: string, path: string, file: File) {
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file, {
        cacheControl: '3600',
        upsert: true
      })
    
    if (error) throw error
    return data
  },

  // Download file from storage
  async downloadFile(bucket: string, path: string) {
    const { data, error } = await supabase.storage
      .from(bucket)
      .download(path)
    
    if (error) throw error
    return data
  },

  // Get public URL for file
  getPublicUrl(bucket: string, path: string) {
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(path)
    
    return data.publicUrl
  },

  // Delete file from storage
  async deleteFile(bucket: string, path: string) {
    const { error } = await supabase.storage
      .from(bucket)
      .remove([path])
    
    if (error) throw error
    return true
  },

  // List files in bucket/folder
  async listFiles(bucket: string, folder?: string) {
    const { data, error } = await supabase.storage
      .from(bucket)
      .list(folder || '')
    
    if (error) throw error
    return data
  },

  // Create signed URL for upload
  async createSignedUploadUrl(bucket: string, path: string, options?: any) {
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUploadUrl(path, options)
    
    if (error) throw error
    return data
  },

  // Create signed URL for download
  async createSignedDownloadUrl(bucket: string, path: string, expiresIn?: number) {
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, expiresIn || 3600)
    
    if (error) throw error
    return data
  }
}

// Validate environment variables
export function validateSupabaseConfig() {
  const required = [
    'VITE_SUPABASE_URL',
    'VITE_SUPABASE_ANON_KEY'
  ]
  
  const missing = required.filter(key => !import.meta.env[key])
  
  if (missing.length > 0) {
    throw new Error(`Missing required environment variables: ${missing.join(', ')}`)
  }
  
  return true
}
